import { Box, IconButton, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { onboardingTokens } from "../../theme";

/**
 * A search box that knows nothing about what it is searching.
 *
 * Fully controlled, so the component doing the filtering owns the text — here
 * that is `AviationSectorPicker`, its only consumer, which is why it sits
 * beside it. Promote it up to `components/` if a second list ever wants one.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {() => void} props.onClear
 * @param {string} props.placeholder
 * @param {string} props.label  accessible name; there is no visible <label>
 */
const SearchField = ({ value, onChange, onClear, placeholder, label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      px: 1.75,
      py: 1.25,
      borderRadius: "0.75rem",
      backgroundColor: onboardingTokens.card,
      border: `1.5px solid ${onboardingTokens.track}`,
      "&:focus-within": { borderColor: onboardingTokens.teal },
    }}
  >
    <SearchIcon
      aria-hidden="true"
      sx={{
        fontSize: 18,
        flexShrink: 0,
        color: onboardingTokens.mutedForeground,
      }}
    />

    <InputBase
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
      placeholder={placeholder}
      inputProps={{ "aria-label": label }}
      sx={{ flex: 1, fontSize: "0.9rem" }}
    />

    {/* Only rendered when there is something to clear, so it never sits there
        as a dead control. */}
    {value && (
      <IconButton
        type="button"
        onClick={onClear}
        aria-label={label ? `Clear ${label.toLowerCase()}` : "Clear search"}
        size="small"
        sx={{ flexShrink: 0, color: onboardingTokens.mutedForeground }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
);

export default SearchField;
